/* eslint-disable react/prop-types */
import React, { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Form } from 'react-bootstrap';
import { Card } from 'react-bootstrap';
import { faAddressCard } from '@fortawesome/free-regular-svg-icons';
import { faDatabase, faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from 'components/base/Button';
import TooltipIconButton from 'components/common/TooltipIconButton';
import IconButton from 'components/custom/ui/IconButton';

const StyledTable = styled.table`
  /* border-radius: 10px; */
  overflow: hidden;
  border-collapse: separate;
  border-spacing: 0;

  th,
  td:not(.td-container) {
    padding: 12px 15px !important;
    text-align: left;
    border: 0;
  }

  th {
    /* background-color: var(--phoenix-gray-100); */
    font-weight: 400;
    text-align: center;
  }

  thead th:first-of-type {
    /* border-top-left-radius: 10px; */
    font-weight: 300;
    text-align: start;
    padding-left: 32px !important;
  }

  tbody td:first-of-type:not(.td-container) {
    /* border-top-left-radius: 10px; */
    padding-left: 32px !important;
  }

  thead th:last-of-type {
    /* border-top-right-radius: 10px; */
  }

  tbody tr:last-child td:first-of-type {
    /* border-bottom-left-radius: 10px; */
  }

  tbody tr:last-child td:last-of-type {
    /* border-bottom-right-radius: 10px; */
  }

  .td-container {
    padding: 0px !important;
    border-bottom: 0px;
  }
`;

const EmployeePermission = () => {
  const [permissions, setPermissions] = useState([
    {
      group_name: 'HRMS Management',
      icon: faAddressCard,
      modules: [
        {
          name: 'Manage Groups',
          view: true,
          create: true,
          edit: true,
          delete: true
        },
        {
          name: 'Manage Employees',
          view: true,
          create: true,
          edit: true,
          delete: true
        },
        {
          name: 'Manage Attendance',
          view: true,
          create: true,
          edit: false,
          delete: false
        },
        {
          name: 'Manage Leave',
          view: true,
          create: true,
          edit: true,
          delete: true
        },
        {
          name: 'Manage Salary',
          view: true,
          create: true,
          edit: true,
          delete: false
        },
        {
          name: 'Manage Bonus',
          view: true,
          create: true,
          edit: true,
          delete: false
        }
      ]
    },
    {
      group_name: 'Payment Management',
      icon: faDatabase,
      modules: [
        {
          name: 'Coal and Iron ore',
          view: true,
          create: true,
          edit: true,
          delete: true
        },
        {
          name: 'FlyAsh',
          view: true,
          create: true,
          edit: true,
          delete: true
        }
      ]
    }
  ]);

  const handlePermissionChange = (
    id,
    index,
    field,
    value,
    isParent = false
  ) => {
    setPermissions(prevPermissions => {
      const updatedPermissions = [...prevPermissions];

      // If the parent (group header) checkbox is clicked
      if (isParent) {
        updatedPermissions[id]['modules'].forEach(module => {
          module[field] = value;
        });
      } else {
        // If an individual child checkbox is clicked
        updatedPermissions[id]['modules'][index][field] = value;
      }

      return updatedPermissions;
    });
  };

  return (
    <div className="">
      <Card className="">
        <Card.Body className="p-0">
          <div className="d-flex justify-content-between border-bottom px-4 py-2 align-items-center">
            <h4 className="text-800 mb-0">Admin</h4>
            <Button variant="link" className="px-0">
              <FontAwesomeIcon icon={faPlus} className="me-2" /> Add Permission
            </Button>
          </div>
          <StyledTable className="table mb-0">
            <thead>
              <tr>
                <th className="text-800">Permission</th>
                <th className="text-800">View</th>
                <th className="text-800">Create</th>
                <th className="text-800">Edit</th>
                <th className="text-800">Delete</th>
                <th className="text-800"></th>
              </tr>
            </thead>
            <tbody>
              {permissions.map((permission, i) => (
                <GroupRow
                  key={`${i}-row-group`}
                  i={i}
                  permission={permission}
                  allPermission={permissions}
                  handlePermissionChange={handlePermissionChange}
                />
              ))}
            </tbody>
          </StyledTable>
        </Card.Body>
      </Card>
    </div>
  );
};

const GroupRow = ({ handlePermissionChange, i, permission, allPermission }) => {
  const [isExpand, setIsExpand] = useState(true);

  // Track indeterminate state for each column
  const [indeterminateState, setIndeterminateState] = useState({
    view: false,
    create: false,
    edit: false,
    delete: false
  });

  const handleExpand = () => {
    setIsExpand(!isExpand);
  };

  // Update parent checkbox state based on the current permission state
  useEffect(() => {
    const updateParentCheckboxState = field => {
      const allChecked = permission.modules.every(module => module[field]);
      const someChecked = permission.modules.some(module => module[field]);

      setIndeterminateState(prev => ({
        ...prev,
        [field]: someChecked && !allChecked // Indeterminate if some are checked but not all
      }));
    };

    ['view', 'create', 'edit', 'delete'].forEach(field => {
      updateParentCheckboxState(field);
    });
  }, [allPermission]); // Run this effect whenever the `permission` prop changes

  // Handle parent checkbox change
  const handleParentCheckboxChange = (field, checked) => {
    handlePermissionChange(i, 0, field, checked, true); // Update all children
    setIndeterminateState(prev => ({ ...prev, [field]: false })); // Parent checkbox is no longer indeterminate after change
  };

  // Handle child checkbox change
  const handleChildCheckboxChange = (moduleIndex, field, checked) => {
    handlePermissionChange(i, moduleIndex, field, checked); // Update permission
  };

  // Refs for parent checkboxes to manually set indeterminate property
  const viewRef = useRef();
  const createRef = useRef();
  const editRef = useRef();
  const deleteRef = useRef();

  // Set the indeterminate state using refs after render
  useEffect(() => {
    if (viewRef.current) {
      viewRef.current.indeterminate = indeterminateState.view;
    }
    if (createRef.current) {
      createRef.current.indeterminate = indeterminateState.create;
    }
    if (editRef.current) {
      editRef.current.indeterminate = indeterminateState.edit;
    }
    if (deleteRef.current) {
      deleteRef.current.indeterminate = indeterminateState.delete;
    }
  }, [indeterminateState]);

  return (
    <>
      <tr key={`${i}-group`} className="bg-body border-top border-bottom  p-0">
        <td
          className="fs-9 fw-bold text-1100 border-top border-bottom cursor-pointer"
          onClick={handleExpand}
        >
          <div className="w-100 d-flex align-items-center">
            <IconButton
              variant="text"
              icon={isExpand ? 'mingcute:up-line' : 'mingcute:down-line'}
              className="p-0 me-2 ms-n4"
              iconProp={{ fontSize: '20px' }}
            />
            <div>
              <span className="text-primary me-3">
                <FontAwesomeIcon
                  icon={permission.icon}
                  transform={{ size: 20 }}
                />
              </span>
              {permission.group_name}
            </div>
          </div>
        </td>

        {/* Parent checkboxes with indeterminate state */}
        <td className="text-center border-top border-bottom">
          <Form.Check
            type="checkbox"
            ref={viewRef} // Attach ref to checkbox
            checked={permission.modules.every(module => module.view)} // Parent checkbox state
            onChange={e => handleParentCheckboxChange('view', e.target.checked)}
          />
        </td>
        <td className="text-center border-top border-bottom">
          <Form.Check
            type="checkbox"
            ref={createRef} // Attach ref to checkbox
            checked={permission.modules.every(module => module.create)} // Parent checkbox state
            onChange={e =>
              handleParentCheckboxChange('create', e.target.checked)
            }
          />
        </td>
        <td className="text-center border-top border-bottom">
          <Form.Check
            type="checkbox"
            ref={editRef} // Attach ref to checkbox
            checked={permission.modules.every(module => module.edit)} // Parent checkbox state
            onChange={e => handleParentCheckboxChange('edit', e.target.checked)}
          />
        </td>
        <td className="text-center border-top border-bottom">
          <Form.Check
            type="checkbox"
            ref={deleteRef} // Attach ref to checkbox
            checked={permission.modules.every(module => module.delete)} // Parent checkbox state
            onChange={e =>
              handleParentCheckboxChange('delete', e.target.checked)
            }
          />
        </td>

        <td className="border-top border-bottom text-center">
          <TooltipIconButton
            title="Remove permission"
            icon={faTrash}
            iconClass="text-danger"
          />
        </td>
      </tr>

      {isExpand &&
        permission.modules.map((module, j) => (
          <tr key={j}>
            <td className="fw-medium text-900 ps-2">{module.name}</td>

            {/* Child checkboxes */}
            {['view', 'create', 'edit', 'delete'].map(field => (
              <td className="text-center fs-9 text-success" key={field}>
                <Form.Check
                  type="checkbox"
                  className="text-success"
                  checked={module[field]} // Child checkbox state
                  onChange={e =>
                    handleChildCheckboxChange(j, field, e.target.checked)
                  } // Update child checkbox
                />
              </td>
            ))}

            <td className="text-center">
              <TooltipIconButton
                title="Remove permission"
                icon={faTrash}
                iconClass="text-danger"
              />
            </td>
          </tr>
        ))}
    </>
  );
};

export default EmployeePermission;
